import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import api from '@/lib/api';

/** Addresses that could only have come from another platform's storefront. */
const OLD_STORE_ADDRESS = /\.(asp|aspx|htm|html|php)$/i;

/**
 * The storefront's "not found" page. A store moved here from another platform
 * still has links to its old addresses all over the web, so before saying
 * "not found" an old-looking address is looked up and, when it names a
 * product, category or page that came over, the visitor is sent there.
 */
export default function NotFound() {
  const { t } = useTranslation('pages');
  const { pathname, search } = useLocation();
  const address = pathname + search;
  const looksOld = OLD_STORE_ADDRESS.test(pathname);
  const [answer, setAnswer] = useState<{ address: string; target: string | null } | null>(null);

  useEffect(() => {
    if (!looksOld) return;
    let current = true;
    api
      .get<{ target: string }>(`/redirects/resolve?path=${encodeURIComponent(address)}`)
      .then((res) => current && setAnswer({ address, target: res.target }))
      .catch(() => current && setAnswer({ address, target: null }));
    return () => {
      current = false;
    };
  }, [address, looksOld]);

  const settled = answer?.address === address ? answer : null;
  if (looksOld && !settled) return <main className="min-h-[50vh]" aria-busy="true" />;
  if (settled?.target) return <Navigate to={settled.target} replace />;

  return (
    <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 text-center">
      <FileText className="mx-auto h-16 w-16 text-stone-300 dark:text-stone-600" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-bold text-ink">{t('notFound.heading')}</h1>
      <p className="mt-2 text-ink-muted">{t('notFound.description')}</p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center rounded-lg bg-sage-600 px-4 py-2 text-sm font-medium text-white hover:bg-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
      >
        {t('notFound.goHome')}
      </Link>
    </main>
  );
}
